import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import * as Sentry from '@sentry/react';

import { PaymentMethod, showSnackbar } from '@trendiolive/trendio-selfcare-ui-kit';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { queryMerchantInfo, setBasicInfo } from 'app/slices/merchantSlice';

import type { MerchantUpdateMutationResultType } from 'model/types/merchant';

import { authenticatedPost } from 'utils/axios';

import { PAYMENT_METHOD_STATUS_SORT_ORDER } from 'features/pricing/constants/paymentMethodFlowConstants';
import { getPendingPaymentMethodUpdateId } from 'features/pricing/utils/pendingPaymentMethodSession';

import { GET_PAYMENT_METHODS } from 'queries/get_payment_methods';
import { UPDATE_MERCHANT_DEFAULT_PAYMENT_METHOD } from 'queries/update_merchant_default_payment_method';

type UsePaymentMethodsOptions = Readonly<{
  /** When provided, re-syncs the pending payment method when the user returns to this tab after Stripe hosted verification. */
  resumePendingPaymentMethodUpdate?: (paymentMethodId: string) => Promise<void>;
}>;

export default function usePaymentMethods(options?: UsePaymentMethodsOptions) {
  const dispatch = useAppDispatch();
  const defaultPaymentMethodId = useAppSelector((store) => store.merchant.merchant?.defaultPaymentMethod?.id);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const location = useLocation();
  const lastProcessedKeyRef = useRef<string | null>(null);
  const isResumingRef = useRef(false);
  const resumePendingPaymentMethodUpdate = options?.resumePendingPaymentMethodUpdate;

  useEffect(() => {
    getPaymentMethods();
  }, []);

  // Refetch when location state indicates a refresh is needed
  useEffect(() => {
    if (location.state?.refreshPaymentMethods && location.key !== lastProcessedKeyRef.current) {
      lastProcessedKeyRef.current = location.key;
      getPaymentMethods();
    }
  }, [location.state, location.key]);

  const refreshPaymentMethods = useCallback(() => {
    getPaymentMethods();
  }, []);

  const handleResumePendingPaymentMethod = useCallback(
    async (paymentMethodId: string) => {
      if (!resumePendingPaymentMethodUpdate) return;

      await resumePendingPaymentMethodUpdate(paymentMethodId);
      refreshPaymentMethods();
    },
    [resumePendingPaymentMethodUpdate, refreshPaymentMethods]
  );

  useEffect(() => {
    if (!resumePendingPaymentMethodUpdate) return;

    async function handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return;

      const pendingPaymentMethodId = getPendingPaymentMethodUpdateId();
      if (!pendingPaymentMethodId || isResumingRef.current) return;

      isResumingRef.current = true;
      try {
        await handleResumePendingPaymentMethod(pendingPaymentMethodId);
      } finally {
        isResumingRef.current = false;
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [resumePendingPaymentMethodUpdate, handleResumePendingPaymentMethod]);

  const handleSetAsDefault = useCallback(
    async (paymentMethodId: string) => {
      if (paymentMethodId === defaultPaymentMethodId) return;

      try {
        const result = (await authenticatedPost({
          query: UPDATE_MERCHANT_DEFAULT_PAYMENT_METHOD,
          vars: { input: { defaultPaymentMethodId: paymentMethodId } }
        })) as { data?: MerchantUpdateMutationResultType };

        if (result.data?.merchantUpdate?.merchant?.defaultPaymentMethod?.id) {
          const merchantState = await queryMerchantInfo();
          if (merchantState.merchant) {
            dispatch(setBasicInfo(merchantState));
          }
          showSnackbar({
            open: true,
            variant: 'alert',
            messageId: 'successfully-updated',
            alertColor: 'success'
          });
        } else {
          showSnackbar({
            open: true,
            variant: 'alert',
            messageId: 'something-went-wrong',
            alertColor: 'error'
          });
        }
      } catch (e) {
        Sentry.captureException(e);
        showSnackbar({
          open: true,
          variant: 'alert',
          messageId: 'something-went-wrong',
          alertColor: 'error'
        });
      }
    },
    [defaultPaymentMethodId, dispatch]
  );

  async function getPaymentMethods() {
    try {
      setIsLoading(true);
      const [paymentMethodsResult, merchantResult] = await Promise.allSettled([
        authenticatedPost({ query: GET_PAYMENT_METHODS }),
        queryMerchantInfo()
      ]);

      if (merchantResult.status === 'fulfilled' && merchantResult.value.merchant) {
        dispatch(setBasicInfo(merchantResult.value));
      } else if (merchantResult.status === 'rejected') {
        Sentry.captureException(merchantResult.reason);
      }

      if (paymentMethodsResult.status === 'fulfilled' && paymentMethodsResult.value.data) {
        const { data } = paymentMethodsResult.value;
        const processingEdges = data.processing?.edges ?? [];
        const requiresActionEdges = data.requiresAction?.edges ?? [];
        const activeEdges = data.active?.edges ?? [];
        const methods = [...processingEdges, ...requiresActionEdges, ...activeEdges].map(
          (edge: { node: PaymentMethod }) => edge.node
        );
        setPaymentMethods(sortPaymentMethods(methods));
      } else if (paymentMethodsResult.status === 'rejected') {
        Sentry.captureException(new Error(`Error fetching payment methods: ${paymentMethodsResult.reason?.message}`));
      }
    } finally {
      setIsLoading(false);
    }
  }

  return { paymentMethods, isLoading, defaultPaymentMethodId, handleSetAsDefault, refreshPaymentMethods };
}

function sortPaymentMethods(methods: PaymentMethod[]): PaymentMethod[] {
  return [...methods].sort((a, b) => {
    const orderA = PAYMENT_METHOD_STATUS_SORT_ORDER[a.status ?? ''] ?? 99;
    const orderB = PAYMENT_METHOD_STATUS_SORT_ORDER[b.status ?? ''] ?? 99;
    return orderA - orderB;
  });
}
